import {
  buildSportsbookBetslipDeeplink,
  buildSportsbookBetslipWebUrl,
} from '@low6-pickem/sports-config';
import { scheduleSportsbookWebFallback } from '@/utils/sportsbookBetslip';

type HowToPlayBetslipLinkProps = {
  /** Contest league code from `pickems.sport` (e.g. 'NFL') — picks the DK CMS page. */
  sport: string | null | undefined;
  /** Sportsbook selection ids to load into the bet slip. */
  selectionIds: readonly (string | null | undefined)[];
  /** Fired on tap, so the How to Play overlay can close behind the handoff. */
  onNavigate?: () => void;
};

/**
 * Quiet "Add to Bet Slip" link at the foot of the How to Play sheet.
 *
 * This exists to exercise DK's production betslip link on production while the main
 * handoff — the pick-card CTA and the post-pick prompt — stays switched off behind
 * `SPORTSBOOK_BETSLIP_ENABLED` (DK changed the link and we need to confirm the new one
 * resolves on a real device). Tucking it inside How to Play keeps it findable by anyone
 * testing without putting a second bet-slip call to action in front of every player.
 *
 * Always targets PRODUCTION, never the per-environment target the pick-card CTA
 * resolves: the whole point is to test the link DK confirmed for the live app, from
 * whichever build the tester happens to be on.
 *
 * The handoff itself is the same two-stage one as {@link SportsbookBetslipCta} — a real
 * anchor so the OS can claim `dksb://` without tearing down the SPA (DKPLAT-778), with a
 * web-sportsbook fallback a beat later if nothing does. Renders nothing when no link can
 * be built, so an unsupported sport never opens the NFL bet slip.
 */
export default function HowToPlayBetslipLink({
  sport,
  selectionIds,
  onNavigate,
}: HowToPlayBetslipLinkProps) {
  const deeplink = buildSportsbookBetslipDeeplink(sport, selectionIds, 'production');
  const webUrl = buildSportsbookBetslipWebUrl(sport, selectionIds);
  if (!deeplink) return null;

  return (
    <div className="mt-5 border-t border-white/10 pt-4">
      <a
        href={deeplink}
        // The handoff must leave OUR frame: Daily Reveal runs inside a DK-owned frame and a
        // subframe is not allowed to launch an external scheme.
        target="_top"
        rel="noopener noreferrer"
        // Not `preventDefault`: the anchor must still offer the scheme to the OS.
        onClick={() => {
          onNavigate?.();
          scheduleSportsbookWebFallback(webUrl);
        }}
        aria-label="Add to Bet Slip — opens DraftKings Sportsbook with this selection loaded"
        data-test-id="how-to-play-betslip-link"
        // `select-none` stops Android's Touch to Search turning a tap into a Google lookup.
        className="block select-none text-[12px] font-semibold text-[#53d337] underline underline-offset-2 transition-colors hover:text-[#70e455]"
      >
        Add to Bet Slip
      </a>
      <span className="mt-1 block text-[10px] font-medium text-[#8a8a8a]">
        Opens DraftKings Sportsbook with this selection loaded.
      </span>
    </div>
  );
}
